<template>
  <div
    class="fa-width-auto entry"
    :app-language="selectedLanguage"
    :app-name="appName$"
    :app-version="version$"
  >
    <template v-if="!isReadyWithPartner">
      <loading-alt />
    </template>
    <template v-else>
      <div class="entry__header">
        <div class="entry__title">
          <div
            class="h3"
            v-text="isCollectiveContract ? $t('entry.title.collective') : $t('entry.title.dvp')"
          />
          <div
            v-if="isCollectiveContract"
            class="title-3"
            v-text="collectiveCompanyName"
          />
        </div>

        <div
          v-if="isLoginEnabled"
          class="entry__login"
        >
          <login-action
            ref="loginRef"
            modal-id="entry-widget-login-modal"
            navigate-to-dvp
            :channel="CHANNEL.ENTRYWIDGET"
          />
        </div>
      </div>

      <basic-form
        :auto-focus="false"
        :validator="v"
        class="entry__form"
        name="entry"
        @submit="submit"
      >
        <basic-location
          v-model="formData.address"
          class="entry__location"
          name="address"
          :contract-start-date="basketStore.basket.contractStartDate"
          :disabled="hasBasket"
          :label="$t('person.plz')"
          :supporting-text="$t('location.placeholder')"
          :v="v.address"
        />

        <div class="entry__date">
          <basic-date-input
            v-model="formData.dateOfBirth"
            name="dateOfBirth"
            :disabled="hasBasket"
            :label="birthdateText"
            :placeholder="$t('form.dateHint')"
            :v="v.dateOfBirth"
          />
        </div>

        <basic-gender
          v-model="formData.gender"
          class="entry__gender"
          name="gender"
          :readonly="hasBasket"
          :age="DateTimeUtil.getAge(formData.dateOfBirth)"
          :v="v.gender"
        />

        <template v-if="formData.gender === GENDER.UNBORN">
          <div class="entry__info">
            <basic-message :content="$t('person.unbornMessage')" />
          </div>
        </template>

        <div
          class="entry__buttons"
          :class="{ 'entry__buttons--reversed': hasBasket }"
        >
          <basic-link
            v-if="hasBasket"
            class="entry__restart"
            hierarchy="tertiary"
            :label="$t('entry.restart')"
            @click="restartApplication"
          >
            <template #trailing>
              <the-icon
                art="solid"
                name="arrow-rotate-left"
              />
            </template>
          </basic-link>

          <aide-banner
            is-button
            :is-collective="!!$props.collectiveContract"
            :source="SOURCE.ENTRY_WIDGET"
            :collective-help-link-de="$props.collectiveHelpLinkDe"
            :collective-help-link-en="$props.collectiveHelpLinkEn"
            :collective-help-link-it="$props.collectiveHelpLinkIt"
            :collective-help-link-fr="$props.collectiveHelpLinkFr"
            @click="alvaLink => gotoAlva(alvaLink)"
          />

          <basic-button
            class="entry__submit"
            type="submit"
            :label="$t('entry.button')"
          />
        </div>
      </basic-form>
    </template>
  </div>
</template>

<script setup>
  import { computed, inject, reactive, ref, watchEffect } from 'vue'
  import useVuelidate from '@vuelidate/core'
  import { v4 as uuidv4 } from 'uuid'

  import { events$, loading$ } from '@shared/services'

  import useABConfig from '@shared/hooks/useABConfig'
  import useApplication from '@shared/hooks/useApplication'
  import useAuth from '@shared/hooks/useAuth'
  import useBasket from '@shared/hooks/useBasket'
  import useI18n from '@shared/hooks/useI18n'
  import useSnackbar from '@shared/hooks/useSnackbar'
  import { ReactivityUtil } from '@shared/utils/Reactivity'
  import { DateTimeUtil } from '@shared/utils/DateTime'
  import { createDistributionPartners } from '@shared/helpers/Partner'

  import basketStore from '@shared/store/basket'
  import partnerStore from '@shared/store/partner'
  import partnerAPI from '@shared/api/partner'

  import {
    BasicButton,
    BasicDateInput,
    BasicForm,
    BasicGender,
    BasicLocation,
    BasicMessage,
    BasicLink,
  } from '@shared/components/Basic'
  import AideBanner from '@shared/components/Aide/Banner'
  import LoadingAlt from '@shared/components/Container/LoadingAlt'
  import { LoginAction } from '@shared/components/Login'

  import validations from '@shared/validations/entryWidget'
  import { CHANNEL, DEFAULT_MASTER_TREATY, GENDER, NOTIFICATION } from '@shared/config/constants'
  import { EVENT_PERSONS_FORM_DISPLAYED, EVENT_TRACKING, SOURCE } from '@shared/config/events'

  // HOOKS
  const { getDvpUrl, isReadyWithPartner } = useApplication()
  const { selectedLanguage, t } = useI18n()
  const { restartApplication } = useAuth()
  const { createDefaultBasket } = useBasket()
  const { addNotification } = useSnackbar()
  const { abConfig } = useABConfig()

  // INIT
  const appName$ = inject('appName$')
  const version$ = inject('version$')
  const formData = defineModel({
    type: Object,
    default: reactive({
      dateOfBirth: null,
      gender: null,
      address: {},
      personId: null,
    }),
  })

  const props = defineProps({
    category: {
      type: String,
      default: null,
    },

    collectiveContract: {
      type: String,
      default: null,
    },

    collectiveHelpLinkDe: {
      type: String,
      default: null,
    },

    collectiveHelpLinkEn: {
      type: String,
      default: null,
    },

    collectiveHelpLinkIt: {
      type: String,
      default: null,
    },

    collectiveHelpLinkFr: {
      type: String,
      default: null,
    },
  })

  // DATA
  const loginRef = ref(null)
  const collectiveCompanyName = ref(null)

  // COMPUTED
  const birthdateText = computed(() => {
    return isUnborn.value ? t('person.estimatedBirthdate') : t('person.birthdate')
  })

  const collectiveContractNumber = computed(() => {
    return props.collectiveContract ?? basketStore.basket.collective?.contractNumber
  })

  const hasBasket = computed(() => {
    return basketStore.basket.basketId && basketStore.basket.persons.length > 0
  })

  const isCollectiveContract = computed(() => {
    return (
      (hasBasket.value && basketStore.basket.collective?.contractNumber) || (!hasBasket.value && props.collectiveContract)
    )
  })

  const isLoginEnabled = computed(() => {
    const isLoginModalActive = loginRef.value?.actionModalRef?.isActive

    return (
      (![CHANNEL.DIGITALADVISOR, CHANNEL.ALVA].includes(basketStore.basket.channel) &&
        !partnerStore.isPrivateInsurer.value &&
        !partnerStore.isBroker.value &&
        !partnerStore.isCollective.value &&
        !basketStore.basket.existingCustomer) ||
      isLoginModalActive
    )
  })

  const isUnborn = computed(() => {
    return formData.value.gender === GENDER.UNBORN
  })

  // METHODS
  async function getCollectiveCompanyName() {
    const partnerInfo = await partnerAPI.getPartner({
      collectiveNumber: collectiveContractNumber.value,
      contractStartDate: basketStore.basket.contractStartDate,
      language: selectedLanguage.value,
    })

    return partnerInfo.collectiveContractConfig.collectiveTreatyPartnerName
  }

  function gotoAlva(alvaLink) {
    if (v.value.$invalid || hasBasket.value) {
      navigate(alvaLink)
    } else {
      submit(alvaLink)
    }
  }

  function navigate(target = false) {
    if (!target) {
      target = getDvpUrl(selectedLanguage.value)

      if (props.category) {
        target += '/persons?gotoFromPersons=products/:personId/' + props.category
      } else if (abConfig.value.entryWidgetTarget) {
        target += '/' + abConfig.value.entryWidgetTarget
      }
    }

    window.open(target, '_self').focus()
  }

  function createDefaultBasketPayload() {
    const personId = formData.value.personId ?? uuidv4()
    return {
      address: formData.value.address,
      channel: CHANNEL.ENTRYWIDGET,
      language: selectedLanguage.value.toUpperCase(),
      masterTreaty: DEFAULT_MASTER_TREATY,
      persons: [
        {
          contractModified: true,
          personId,
          immigrant: false,
          personData: {
            dateOfBirth: formData.value.dateOfBirth,
            gender: formData.value.gender,
          },
        },
      ],
    }
  }

  async function createCollectiveBasketPayload(collectiveContract) {
    const personId = formData.value.personId ?? uuidv4()
    const payload = await createDefaultBasket({
      address: formData.value.address,
      channel: CHANNEL.PARTNERDVP,
      language: selectedLanguage.value.toUpperCase(),
      persons: [
        {
          contractModified: true,
          personId,
          immigrant: false,
          personData: {
            dateOfBirth: formData.value.dateOfBirth,
            gender: formData.value.gender,
          },
        },
      ],
    })

    const partnerData = await partnerStore.install({
      contractStartDate: payload.contractStartDate,
      language: selectedLanguage.value.toUpperCase(),
      collectiveNumber: collectiveContract,
      partnerNumber: null,
      distributionPartnerNumber: null,
    })

    const cConfig = partnerData.collectiveContractConfig

    payload.persons[0].collectivePersonGroup = cConfig.personGroups[0].groupNumber
    payload.distributionPartners = createDistributionPartners(cConfig.distributionPartnerSanitas)

    // ['infoText'] not an allowed key for the collective-property
    delete cConfig.infoText

    // @note: isn't here only channel and contractNumber needed?
    payload.collective = cConfig

    if (cConfig.masterTreaty) {
      payload.masterTreaty = cConfig.masterTreaty
    }

    return payload
  }

  async function submit(target = false) {
    const shouldResume = !!formData.value.personId
    const collectiveContract = props.collectiveContract

    if (shouldResume) {
      navigate()
    } else {
      loading$.start()

      let payload
      if (collectiveContract && collectiveContract.length > 0) {
        payload = await createCollectiveBasketPayload(collectiveContract)
      } else {
        payload = createDefaultBasketPayload()
      }

      events$.emit(EVENT_PERSONS_FORM_DISPLAYED, { source: CHANNEL.ENTRYWIDGET })
      return basketStore
        .updateOvpBasket(payload)
        .then(() => {
          loading$.end()
          events$.emit(EVENT_TRACKING.ENTERED, { source: CHANNEL.ENTRYWIDGET })
          navigate(target)
        })
        .catch(error => {
          loading$.failed()
          if (error?.source !== SOURCE.BACKEND) {
            addNotification({
              text: error,
              type: NOTIFICATION.ERROR,
            })
          }
        })
    }
  }

  // VALIDATION
  const v = useVuelidate(validations, formData.value, { $autoDirty: true })

  // LIFECYCLE HOOKS
  watchEffect(async () => {
    const person = basketStore.basket.persons?.[0] ?? false

    if (person) {
      formData.value.dateOfBirth = person.personData.dateOfBirth
      formData.value.gender = person.personData.gender
      formData.value.address = ReactivityUtil.clone(basketStore.basket.address)
      formData.value.personId = person.personId
    }

    if (collectiveContractNumber.value) {
      collectiveCompanyName.value = await getCollectiveCompanyName()
    }
  })
</script>

<style scoped>
.entry {
  border-radius: var(--border-radius-res-m);
  background: var(--surface);
  padding: var(--dotcom-responsive-spacing-res-m) var(--dotcom-responsive-offset-responsive);
  box-shadow: var(--elevation-level-3);
  min-width: 320px;

  display: grid;
  grid-template-areas:
    'title'
    'login'
    'form';
}

.entry__title {
  display: grid;
  grid-area: title;
  margin: 0 0 var(--fixed-spacing-fix-04);
  gap: var(--fixed-spacing-fix-01);
}

.entry__form {
  grid-area: form;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    'location'
    'date'
    'gender'
    'info'
    'buttons';
}

.entry__location {
  grid-area: location;
}

.entry__date {
  grid-area: date;
}

.entry__gender {
  grid-area: gender;
}

.entry__info {
  grid-area: info;
  margin: 0 0 var(--fixed-spacing-fix-06);
}
.entry__buttons {
  grid-area: buttons;
  display: grid;
  gap: var(--fixed-spacing-fix-06);
  grid-auto-flow: column;
  grid-template-areas:
    'submit'
    'banner';

  &--reversed {
    grid-template-areas:
      'submit'
      'restart'
      'banner';
  }

  .entry__restart {
    grid-area: restart;
  }

  .banner {
    grid-area: banner;
  }

  .entry__submit {
    grid-area: submit;
  }
}

.entry__restart {
  display: flex;
  justify-content: center;
}

.entry__login {
  grid-area: login;
  margin: 0 0 var(--fixed-spacing-fix-06);
  display: flex;
  align-items: center;
}

@media (--v-medium) {
  .entry {
    padding: var(--dotcom-responsive-spacing-res-m) var(--dotcom-responsive-spacing-res-xl);
  }

  .entry__form {
    grid-template-areas:
      'location location'
      'date date'
      'gender gender'
      'info info'
      'buttons buttons';
  }

  .entry__buttons {
    grid-template-areas: 'banner submit';

    &--reversed {
      grid-template-columns: 1fr min-content;
      grid-template-areas:
        'restart submit'
        'banner banner';
    }

    .entry__restart {
      justify-self: start;
      grid-area: restart;
    }

    .banner {
      grid-area: banner;
    }

    .entry__submit {
      align-self: center;
      grid-area: submit;
    }
  }

  .entry__login:deep(.prompt) {
    align-items: flex-start;
    flex-direction: column;
    gap: 0;
  }
}

@media (--v-large) {
  .entry__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 0 0 var(--fixed-spacing-fix-06);
  }

  .entry__title,
  .entry__login {
    margin: 0;
  }

  .entry__info {
    margin: var(--fixed-spacing-fix-06) 0 0;
  }

  .entry__form {
    grid-template-areas:
      'location date gender'
      'buttons buttons buttons'
      'info info info';
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: var(--fixed-spacing-fix-06);
  }

  .entry__restart {
    margin-right: inherit;
  }

  .entry__login:deep(.prompt) {
    align-items: center;
    flex-direction: row;
    gap: var(--fixed-spacing-fix-06);
  }
}
</style>
